import * as Sentry from "@sentry/vue";

import gaiupApolloClient from "@/graphql/services/ApolloClient";
import UpsertUserProjectViewMutation from "@/graphql/mutations/UpsertUserProjectViewMutation";
import useHasuraStore from "@/stores/hasura/hasura.store";

const DEBOUNCE_MS = 5000;

const lastTrackedAt = new Map<string, number>();

export const trackProjectView = async (projectId: string | undefined | null): Promise<void> => {
  if (!projectId) return;

  const now = Date.now();
  const last = lastTrackedAt.get(projectId);
  if (last && now - last < DEBOUNCE_MS) return;
  lastTrackedAt.set(projectId, now);

  // The router fires this on project-route entry, which on a cold load / hard reload can beat the
  // async Hasura-token fetch (see hasura.store). Firing now would send the mutation with no JWT and
  // get rejected. Wait for the token; if it never settles, skip and let the next navigation retry.
  const token = await useHasuraStore().awaitHasuraToken();
  if (!token) {
    lastTrackedAt.delete(projectId);
    return;
  }

  try {
    await gaiupApolloClient.mutate({
      mutation: UpsertUserProjectViewMutation,
      variables: { projectId },
      // Refetch by operation name so every active observer of GetRecentProjects
      // (e.g. the sidebar, running with { limit: 5 }) refreshes with its own
      // variables. Passing { query } without variables would refetch under a
      // different cache key and leave the live sidebar query stale.
      refetchQueries: ["GetRecentProjects"],
      awaitRefetchQueries: false,
    });
  } catch (error) {
    // Best-effort tracking — never surface to the user. Reset the debounce
    // entry so the next navigation retries.
    lastTrackedAt.delete(projectId);
    Sentry.captureException(error);
  }
};
